import React from 'react'
import PropTypes from 'prop-types'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { resolveLessonCompletionReportPathForSite } from 'store/licenseManager/reportRouteHelpers'
import K8ReportingLeaderTools from 'components/K8ReportingLeaderTools'
import {
  DEFAULT_K8_REPORT_UNIT_SECTION,
  K8_REPORT_UNIT_SECTION,
} from 'pages/LessonCompletionReport/flag.on/constants'
import { isFlagOn } from 'utils/featureFlags'
import {
  ADMIN_PATH,
  buildDwbReportSitePath,
  buildK8ReportSitePath,
  FEATURE_SPECIALIZED_UNITS_TAB,
  LESSON_COMPLETION_REPORTS_TITLE,
  SCHOOL_SUMMARY_BREADCRUMB,
} from './constants'
import {
  BottomWrapper,
  HeaderWrapper,
  LeaderToolsWrapper,
  PageHeaderInnerWrapper,
  PageHeaderWrapper,
  StyledBackLink,
  StyledHairline,
  StyledHeaderTwo,
  StyledReportsTabSet,
  StyledProgramDropdown,
  StyledReportCoreUnitsTabButton,
  StyledReportsSpecializedUnitsTabButton,
  TitleAndSiteRow,
  TopWrapper,
} from './component.styles'

const ReportsPageHeader = ({
  entry,
  gradeNameForUnitNavigation,
  hasDwbAccess,
  isDwbReportRoute,
  isSchoolSummary,
  k8ReportUnitSection,
  navigateTo,
  navigationHandler,
  navigationEvent,
  reportSites,
  selectedSite,
  selectedSiteOption,
  siteOptions,
  updateSelectedSite,
}) => {
  const effectiveUnitSection =
    k8ReportUnitSection || DEFAULT_K8_REPORT_UNIT_SECTION

  const urlText = isSchoolSummary
    ? ADMIN_PATH
    : isDwbReportRoute
    ? buildDwbReportSitePath(selectedSite)
    : buildK8ReportSitePath(effectiveUnitSection, selectedSite)

  const handleBreadcrumbLink = () => {
    navigateTo(urlText)
  }

  const handleSiteChange = e => {
    updateSelectedSite(e.key)
    const targetSite = reportSites?.find(
      site => site.id === e.key || String(site.id) === String(e.key),
    )
    const targetPath = resolveLessonCompletionReportPathForSite(
      targetSite,
      e.key,
    )
    const currentPathBase = isDwbReportRoute
      ? '/reports/dwb'
      : `/reports/k8/${effectiveUnitSection}`

    if (!targetPath.startsWith(currentPathBase)) {
      navigationEvent(targetPath, NavigationHelper.types.REPLACE)
      return
    }

    navigationHandler({ siteId: e.key })
  }

  const handleUnitSectionNavigate = targetSection => {
    if (
      !navigationHandler ||
      !selectedSite ||
      targetSection === effectiveUnitSection
    ) {
      return
    }
    const params = { siteId: selectedSite, unitSection: targetSection }
    if (gradeNameForUnitNavigation) {
      params.gradeName = gradeNameForUnitNavigation
    }
    navigationHandler(params, NavigationHelper.types.REPLACE)
  }

  const handleUnitTabKeyDown = (e, targetSection) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleUnitSectionNavigate(targetSection)
    }
  }

  const siteSelectOptions = siteOptions || []

  const showSpecializedUnitsTab =
    isFlagOn([FEATURE_SPECIALIZED_UNITS_TAB]) && hasDwbAccess

  return (
    <PageHeaderWrapper>
      <PageHeaderInnerWrapper>
        <TopWrapper>
          <StyledBackLink
            dataTestId="breadcrumb-link"
            onClick={handleBreadcrumbLink}
          >
            {SCHOOL_SUMMARY_BREADCRUMB}
          </StyledBackLink>
        </TopWrapper>
        <BottomWrapper isSchoolSummary={isSchoolSummary}>
          <HeaderWrapper>
            <TitleAndSiteRow>
              <StyledHeaderTwo dataTestId="reports-page-title">
                {LESSON_COMPLETION_REPORTS_TITLE}
              </StyledHeaderTwo>
              <StyledProgramDropdown
                dataTestId="select-site-page-header"
                keyProp="key"
                label="School"
                labelProp="name"
                name="Select School"
                onChange={handleSiteChange}
                options={siteSelectOptions}
                placeholder="Enter School Name"
                selectedOption={selectedSiteOption}
              />
            </TitleAndSiteRow>
            <StyledHairline />
          </HeaderWrapper>
          <LeaderToolsWrapper isSchoolSummary={isSchoolSummary}>
            <K8ReportingLeaderTools
              entry={entry}
              isSchoolSummary={isSchoolSummary}
              navigationEvent={navigationEvent}
            />
          </LeaderToolsWrapper>
          <StyledReportsTabSet>
            {!isDwbReportRoute && (
              <StyledReportCoreUnitsTabButton
                className={'className'}
                isActive={
                  effectiveUnitSection === K8_REPORT_UNIT_SECTION.CORE_UNITS
                }
                label="Core Units"
                onClick={() =>
                  handleUnitSectionNavigate(K8_REPORT_UNIT_SECTION.CORE_UNITS)
                }
                onKeyDown={e =>
                  handleUnitTabKeyDown(e, K8_REPORT_UNIT_SECTION.CORE_UNITS)
                }
              />
            )}
            {showSpecializedUnitsTab && (
              <StyledReportsSpecializedUnitsTabButton
                $isDwbOnlyTab={isDwbReportRoute}
                className={'className'}
                isActive={
                  effectiveUnitSection ===
                  K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS
                }
                label="Specialized Units"
                onClick={() =>
                  handleUnitSectionNavigate(
                    K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS,
                  )
                }
                onKeyDown={e =>
                  handleUnitTabKeyDown(
                    e,
                    K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS,
                  )
                }
              />
            )}
          </StyledReportsTabSet>
        </BottomWrapper>
      </PageHeaderInnerWrapper>
    </PageHeaderWrapper>
  )
}

ReportsPageHeader.propTypes = {
  entry: PropTypes.object,
  gradeNameForUnitNavigation: PropTypes.string,
  hasClassesWithCompletedLessonLastPeriod: PropTypes.bool,
  hasDwbAccess: PropTypes.bool,
  isDwbReportRoute: PropTypes.bool,
  isSchoolSummary: PropTypes.bool,
  k8ReportUnitSection: PropTypes.string,
  navigateTo: PropTypes.func,
  navigationEvent: PropTypes.func,
  navigationHandler: PropTypes.func,
  reportSites: PropTypes.arrayOf(PropTypes.object),
  selectedSite: PropTypes.number,
  selectedSiteOption: PropTypes.object,
  siteOptions: PropTypes.arrayOf(PropTypes.object),
  updateSelectedSite: PropTypes.func,
}

export default ReportsPageHeader
